import { useState } from 'react';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from '@frontend/features/common/components/Link';
import { Table } from '@frontend/features/common/components/Table/Table';
import { routes } from '@frontend/router/routes';
import { MetricLabel } from './PerformanceHelp';
import { PerformanceRequest, ResponseReport } from './types';

export function responseMinutes(seconds: number | null | undefined) {
  return seconds === null || seconds === undefined
    ? '—'
    : (seconds / 60).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export function ResponseMetrics({
  report,
  request,
  onRequest,
  clock,
  onClockChange,
}: {
  clock?: 'elapsed' | 'business';
  onClockChange?: (clock: 'elapsed' | 'business') => void;
  report: ResponseReport;
  request: PerformanceRequest;
  onRequest: (request: PerformanceRequest) => void;
}) {
  const { t } = useTranslation();
  const [localClock, setLocalClock] = useState<'elapsed' | 'business'>('elapsed');
  const business = (clock ?? localClock) === 'business';
  const setBusiness = (value: boolean) => {
    const next = value ? 'business' : 'elapsed';
    setLocalClock(next);
    onClockChange?.(next);
  };
  const text = (key: string) => t(`workflowPerformance.${key}`);
  const label = (key: string, help = key) => <MetricLabel label={text(key)} help={text(`help.${help}`)} />;
  const link = (id: number) => <Link to={routes.dashboard.caseView.replace(':id', String(id))}>#{id}</Link>;
  const filter = (values: Partial<PerformanceRequest>) =>
    onRequest({
      ...request,
      afterCaseId: 0,
      afterResponseCaseId: undefined,
      afterResponseId: undefined,
      closingOffset: 0,
      closingDate: undefined,
      ...values,
    });
  return (
    <Stack spacing={2}>
      <Typography variant="h6">{text('responses')}</Typography>
      <Typography>{text('responsesHint')}</Typography>
      <Alert severity="info">{text(business ? 'businessHint' : 'elapsedHint')}</Alert>
      <Stack direction="row" spacing={1}>
        <Button variant={!business ? 'contained' : 'outlined'} onClick={() => setBusiness(false)}>
          {text('elapsedClock')}
        </Button>
        <Button variant={business ? 'contained' : 'outlined'} onClick={() => setBusiness(true)}>
          {text('businessClock')}
        </Button>
      </Stack>
      {(Number(report.progress.unplacedCases) > 0 ||
        Number(report.progress.partialCases) > 0 ||
        Number(report.progress.updatingCases) > 0) && (
        <Alert severity="warning">{t('workflowPerformance.responseProgress', report.progress)}</Alert>
      )}
      {report.issues.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Typography>{text('responseIssues')}</Typography>
          {report.issues.map((row) => (
            <span key={row.caseId}>{link(row.caseId)}</span>
          ))}
        </Stack>
      )}
      {report.groupsTruncated && <Alert severity="warning">{text('groupsTruncated')}</Alert>}
      <Table
        height={360}
        columns={[
          { id: 'advisor', label: text('workloadAdvisor') },
          { id: 'team', label: text('workloadTeam') },
          { id: 'origin', label: text('origin') },
          { id: 'contactedCases', label: label('contactedCases') },
          { id: 'answered', label: label(business ? 'businessAnswered' : 'answered', 'answered') },
          { id: 'mean', label: label('meanMinutes') },
          { id: 'median', label: label('medianMinutes') },
          { id: 'p90', label: label('p90Minutes') },
          { id: 'sla', label: label('sla') },
          { id: 'closedWithoutReply', label: label('closedWithoutReply') },
          { id: 'uncertain', label: label('responseUnverifiedScope', 'unverified') },
        ]}
        data={{
          key: 'id',
          totalCount: report.groups.length,
          items: report.groups.map((row, id) => ({
            ...row,
            id,
            advisor: row.advisor ? (
              <Button onClick={() => filter({ advisor: row.advisor! })}>{row.advisorName ?? row.advisor}</Button>
            ) : (
              text('unassigned')
            ),
            team: row.team ? (
              <Button onClick={() => filter({ team: row.team! })}>{row.teamName ?? row.team}</Button>
            ) : (
              text('unassigned')
            ),
            origin: row.origin
              ? t(`cases.originatedByChannel.${row.origin}`, { defaultValue: row.origin })
              : text('unassigned'),
            answered: business ? `${row.businessAnswered ?? '—'} / ${row.answered}` : row.answered,
            mean: responseMinutes(business ? row.meanBusinessSeconds : row.meanSeconds),
            median: responseMinutes(business ? row.medianBusinessSeconds : row.medianSeconds),
            p90: responseMinutes(business ? row.p90BusinessSeconds : row.p90Seconds),
            contactedCases: report.activityAvailable ? row.contactedCases : '—',
            sla: business
              ? Number(row.businessSlaEvaluated)
                ? `${row.businessWithinThreshold} / ${row.businessSlaEvaluated}`
                : '—'
              : Number(row.slaEvaluated)
                ? `${row.withinThreshold} / ${row.slaEvaluated}`
                : '—',
          })),
        }}
      />
      <Typography variant="h6">{text('responseDetails')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {text('dashboard.responseDetailHint')}
      </Typography>
      <Table
        height={320}
        columns={[
          { id: 'case', label: text('case') },
          { id: 'start', label: label('waitingSince') },
          { id: 'result', label: label('responseResult') },
          { id: 'duration', label: text(business ? 'businessMinutes' : 'elapsedMinutes') },
          { id: 'checked', label: text('lastObserved') },
        ]}
        data={{
          key: 'id',
          totalCount: report.cases.length,
          items: report.cases.map((row) => ({
            ...row,
            id: `${row.caseId}:${row.inboundId}`,
            case: link(row.caseId),
            start: new Date(row.startedAt).toLocaleString(),
            result: text(
              row.partial || row.uncertain
                ? 'unverified'
                : row.answeredAt
                  ? 'responseAnswered'
                  : row.closedAt
                    ? 'closedWithoutReply'
                    : row.transferredAt
                      ? 'responseTransferred'
                      : 'unanswered',
            ),
            duration: responseMinutes(
              business ? (row.partial || row.uncertain ? null : row.businessSeconds) : row.seconds,
            ),
            checked: (business ? row.businessMeasuredUntil : row.checkedAt)
              ? new Date((business ? row.businessMeasuredUntil : row.checkedAt)!).toLocaleString()
              : '—',
          })),
        }}
      />
      <Stack direction="row">
        <Button disabled={!request.afterResponseCaseId} onClick={() => filter({})}>
          {text('firstResponses')}
        </Button>
        <Button
          disabled={!report.next}
          onClick={() =>
            report.next &&
            onRequest({
              ...request,
              afterResponseCaseId: report.next.caseId,
              afterResponseId: report.next.inboundId,
            })
          }
        >
          {text('nextResponses')}
        </Button>
      </Stack>
      <Typography variant="h6">{text('dashboard.waitsNow')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {text('dashboard.waitsNowHint')}
      </Typography>
      <Table
        height={250}
        columns={[
          { id: 'advisor', label: text('workloadAdvisor') },
          { id: 'team', label: text('workloadTeam') },
          { id: 'origin', label: text('origin') },
          { id: 'pending', label: label('unanswered') },
          { id: 'overdue', label: label('overdueElapsed') },
        ]}
        data={{
          key: 'id',
          totalCount: report.groups.length,
          items: report.groups.map((row, id) => ({
            ...row,
            id,
            advisor: row.advisorName ?? row.advisor ?? text('unassigned'),
            team: row.teamName ?? row.team ?? text('unassigned'),
            origin: row.origin
              ? t(`cases.originatedByChannel.${row.origin}`, { defaultValue: row.origin })
              : text('unassigned'),
          })),
        }}
      />
    </Stack>
  );
}
